"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Sparkles, Crown, Book, Star, Eye, Globe, Zap, Users } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function GoldenDawnTarotPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-amber-300">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-medium">🃏 Tarot</span>
                <span className="text-amber-500">/</span>
                <span className="text-sm">Sistemler</span>
                <span className="text-amber-500">/</span>
                <span className="text-sm">Golden Dawn Tarot</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/tarot/systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Tarot Sistemleri
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Golden Dawn Tarot</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-amber-900/20 to-yellow-900/20 rounded-xl p-8 mb-8 border border-amber-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Sparkles className="h-10 w-10 text-amber-400" />
                Golden Dawn Tarot
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Hermetic Order of the Golden Dawn'ın öğretilerine dayanan ezoterik tarot sistemi.
                Modern tarot teorisinin temellerini atan ve correspondence'lar üzerine kurulu olan sistem.
              </p>
            </div>

            {/* Deck Structure */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Deste Yapısı</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Major Arcana */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-700 rounded-lg flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Majör Arkana (22 Kart)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Hermetic Order of the Golden Dawn'un ezoterik öğretileri ve sembolik yapısı ile
                    kabalistik Tree of Life'ın 22 yoluna karşılık gelen büyük sırlar.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Tree of Life'ın 22 yolu ile correspondence</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">İbranice harfler ve astrolojik bağlantılar</span>
                    </div>
                  </div>
                  <Link
                    href="/about/tarot/systems/golden-dawn/major-arcana"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-700 hover:from-amber-600 hover:to-yellow-800 text-white rounded-lg transition-colors text-sm"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    22 Majör Arkana Kartını Gör
                  </Link>
                </div>

                {/* Minor Arcana */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-700 rounded-lg flex items-center justify-center">
                      <Book className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Minör Arkana (56 Kart)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Dört element ve kabalistik dört dünya konsepti üzerine kurulu
                    günlük yaşam kartları. Her suit belirli ezoterik prensipleri temsil eder.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Wands (Ateş), Cups (Su), Swords (Hava), Pentacles (Toprak)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Planetary ve zodiac correspondence sistemli</span>
                    </div>
                  </div>
                  <Link
                    href="/about/tarot/systems/golden-dawn/minor-arcana"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white rounded-lg transition-colors text-sm"
                  >
                    <Book className="h-4 w-4 mr-2" />
                    56 Minör Arkana Kartını Gör
                  </Link>
                </div>
              </div>
            </div>

            {/* Historical Background */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Hermetic Order of the Golden Dawn</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                      <Crown className="h-5 w-5 text-amber-400" />
                      Victorian Era Ezoterizmí
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">19. yüzyıl sonu İngiltere</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Okült bilgilerin sistematik organizasyonu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Çeşitli ezoterik geleneklerin sentezi</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                      <Book className="h-5 w-5 text-amber-400" />
                      Önemli Figürler
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Samuel Liddell MacGregor Mathers</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Arthur Edward Waite</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Aleister Crowley</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Core Teachings */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Öğretiler</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Tree of Life */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Kabalistik Tree of Life</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    10 Sephirot ve 22 yol üzerinden evrenin yapısını açıklayan sistem.
                    Her tarot kartı bu yapı içinde belirli bir konuma sahiptir.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">10 Sephirot (İlahi emanation'lar)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">22 yol (Majör Arkana ile correspondence)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">4 Dünya (Minör Arkana suits'e karşılık)</span>
                    </div>
                  </div>
                </div>

                {/* Correspondences */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Eye className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Correspondence Sistemi</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Her kartın astrolojik, numerolojik, renk, element ve
                    diğer sembolik sistemlerle bağlantıları.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Astrolojik planetary ve zodiac bağlantıları</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">İbranice harf ve numerik değerler</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Renk, element ve taş bağlantıları</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tarot Innovations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarot'a Katkıları</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-700 rounded-lg flex items-center justify-center">
                      <Globe className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Sistematik Yaklaşım</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Tarot kartlarına ilk kez kapsamlı teorik çerçeve getirdi.
                    Her kartın belirli correspondence'ları tanımlandı.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-700 rounded-lg flex items-center justify-center">
                      <Zap className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Divination Teknikleri</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Yapılandırılmış falcılık yöntemleri geliştirdi.
                    Celtic Cross gibi klasik yayılımları sistematikleştirdi.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-700 rounded-lg flex items-center justify-center">
                      <Star className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Ezoterik Entegrasyon</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Kabala, astroloji, alchemy gibi sistemleri tarot ile birleştirdi.
                    Modern ezoterik tarot anlayışının temelini oluşturdu.
                  </p>
                </div>
              </div>
            </div>

            {/* Influence on Modern Tarot */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Modern Tarot'a Etki</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Doğrudan Etkiler</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Rider-Waite-Smith Tarot'un teorik temeli</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Thoth Tarot'un felsefi kaynağı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Modern tarot öğretim müfredatının çekirdeği</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Güncel Kullanım</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Tarot correspondence tablolarında referans</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Profesyonel tarot eğitimlerinde temel</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Academic okült çalışmalarda kaynak</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Methods */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Çalışma Yöntemleri</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">Teorik Çalışma</h3>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li>• Kabalistik Tree of Life öğrenmek</li>
                    <li>• Astrolojik correspondence'ları ezberlemek</li>
                    <li>• İbranice alfabeyi ve numerik değerleri bilmek</li>
                    <li>• Element ve renk sistemlerini anlamak</li>
                    <li>• Planetary ve zodiac bağlantılarını kavramak</li>
                  </ul>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">Pratik Uygulama</h3>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li>• Günlük tek kart çekme ve correspondence inceleme</li>
                    <li>• Sistematik yayılım teknikleri öğrenme</li>
                    <li>• Meditation ve contemplation çalışmaları</li>
                    <li>• Pathworking ve astral projection teknikleri</li>
                    <li>• Kendi kart yorumlarını correspondence'larla karşılaştırma</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
              <p className="text-amber-100 leading-relaxed">
                Golden Dawn Tarot sistemi ezoterik çalışma ve sembolik yorumlama aracıdır ve kesinlik iddiası taşımaz.
                İçerikler tamamen merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için
                uzman görüşü alınmalıdır. Bu açıklamalar sadece tarihi ve öğretici değerin tanıtımı amacıyla sunulmaktadır.
              </p>
            </div>

            {/* Main Tarot Training CTA */}
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-slate-100 mb-3">🎴 Tarot Eğitimleri</h3>
                <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Tarot kart sistemleri, sembolik yorumlama teknikleri, kart dizilimleri ve okuma yöntemleri hakkında
                  detaylı eğitim programları için kayıt olun. Merak ettiğiniz konularda rehberlik alın.
                </p>
                <button className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg">
                  <Users className="h-5 w-5 mr-2" />
                  Tarot Eğitimi İçin Kayıt Ol
                </button>
                <p className="text-slate-400 text-sm mt-3">
                  * Eğitim programları sadece bilgilendirme ve kişisel gelişim amaçlıdır
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/tarot/systems/thoth"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Thoth Tarot
              </Link>

              <Link
                href="/about/tarot/systems/lenormand"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Lenormand Kartları
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
      </div>
    </AboutLayout>
  );
}